import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import type { WebhookProviderCatalogEntry } from '../types';
import { SecretInput } from './secret-input';

/**
 * The secret the provider signs deliveries with. Providers pinned to one
 * delivery URL (`singleWebhookEndpoint`) sign every trigger in the org with the
 * same secret, so once it is on file asking again is not just friction — the
 * install would keep the stored value and quietly discard the re-paste. Those
 * providers get a "saved" state with an explicit rotate instead, which does
 * apply to every trigger sharing the endpoint.
 */
export const WebhookProviderSecretSection: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  value: string;
  onChange: (endpointSecret: string) => void;
}> = ({ catalog, value, onChange }) => {
  const [rotating, setRotating] = useState(false);
  const isGeneric = catalog.key === 'generic';
  const label = isGeneric
    ? `${catalog.displayName} auth token`
    : `${catalog.displayName} signing secret`;

  if (catalog.sharedEndpointSecretConfigured === true && !rotating) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1">
          <Text.P className="text-sm font-medium">{label} saved</Text.P>
          <Text.Caption className="text-muted-foreground mt-0.5 block">
            {catalog.displayName} sends every webhook to one URL for your org,
            so this trigger reuses the secret already on file.
          </Text.Caption>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRotating(true)}
        >
          Replace
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Text.Caption className="text-muted-foreground block">
        {label}
      </Text.Caption>
      <Text.Caption className="text-muted-foreground block text-xs">
        {isGeneric
          ? `Paste the token ${catalog.displayName} should send. Cotera stores it securely and accepts it from a header or query param.`
          : `Paste the signing secret from ${catalog.displayName}. Cotera stores it securely and uses it to verify incoming webhook signatures.`}
      </Text.Caption>
      {rotating ? (
        <Text.Caption className="text-muted-foreground block text-xs">
          Replacing it re-points every {catalog.displayName} webhook in this org
          at the new secret.
        </Text.Caption>
      ) : null}
      <SecretInput value={value} onChange={onChange} placeholder={label} />
      {rotating ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            onChange('');
            setRotating(false);
          }}
        >
          Keep saved secret
        </Button>
      ) : null}
    </div>
  );
};
